import React, { useState } from 'react';
import type { BackendAPITypes } from '../../../../../../../generated/blueprint.ts';
import { useDocumentDrawer } from '../../../../../../../views/DocumentDrawer/DocumentDrawerProvider.tsx';
import { User } from '../../../../../../../api/accessControl/User.ts';
import { PrimaryButton } from '../../../../../../../components/general/PrimaryButton.tsx';
import { PlusIcon } from '@heroicons/react/24/outline';
import { useNotifications } from '../../../../../../../lib/NotificationContext.tsx';
import { fixedAssetLinkErrorMessage } from '../../../../../../../lib/apiErrors.ts';
import { useClientFixedAssets } from '../../../../../../../hooks/useClientFixedAssets.ts';
import { useGLAccounts } from '../../../../../../../hooks/useGLAccounts.ts';
import { SubledgerProvider } from '../../../../../../../contexts/SubledgerContext.tsx';
import FixedAssetTable from './FixedAssetTable';
import CreateFixedAssetModal from './CreateFixedAssetModal';
import type { FixedAssetFormValues } from './fixedAssetForm.ts';
import { LINK_FIXED_ASSETS_FLAG } from './linkFixedAssetsFlag.ts';

export type { FixedAssetFormValues } from './fixedAssetForm.ts';

export type { GlAccount } from '../../../../../../../hooks/useGLAccounts.ts';

export type FixedAsset = BackendAPITypes.BACKENDPreHandleSyncApiV1PartnerGetFixedAssetsGetResponseBody['items'][number];

interface FixedAssetsTabProps {
    currentUser: User;
    clientBusinessId: string;
}

export default function FixedAssetsTab({ currentUser, clientBusinessId }: FixedAssetsTabProps) {
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const { dispatch } = useNotifications();
    const { glAccounts, isLoading: glAccountsLoading } = useGLAccounts(currentUser);
    const { fixedAssets, isLoading, createFixedAsset } = useClientFixedAssets(clientBusinessId);
    const documentDrawer = useDocumentDrawer();
    const canSeeLinkedBills = currentUser.flags.getFlag(LINK_FIXED_ASSETS_FLAG);

    async function handleCreateFixedAsset(values: FixedAssetFormValues) {
        try {
            await createFixedAsset(values);
        } catch (error: unknown) {
            dispatch({
                title: 'Fout bij aanmaken',
                description: fixedAssetLinkErrorMessage(error),
                type: 'error',
            });
            throw error;
        }
        dispatch({
            title: 'Vast actief aangemaakt',
            description: 'Het vast actief is succesvol toegevoegd.',
            type: 'success',
        });
    }

    return (
        <SubledgerProvider user={currentUser} clientBusinessId={clientBusinessId}>
            <div className="p-4">
                <div className="flex justify-between items-center mb-6">
                    <div>
                        <h2 className="text-xl font-semibold text-gray-900">Vaste activa</h2>
                        <p className="text-sm text-gray-500 mt-1">
                            Beheer de vaste activa en afschrijvingsschema's voor deze klant.
                        </p>
                    </div>
                    <PrimaryButton
                        title="Vaste activa toevoegen"
                        icon={PlusIcon}
                        onClick={() => setIsCreateModalOpen(true)}
                    />
                </div>

                <FixedAssetTable
                    items={fixedAssets}
                    isLoading={isLoading}
                    onOpenBill={canSeeLinkedBills
                        ? (documentId) => documentDrawer.open(clientBusinessId, documentId)
                        : undefined}
                />

                <CreateFixedAssetModal
                    open={isCreateModalOpen}
                    onClose={() => setIsCreateModalOpen(false)}
                    onSubmit={handleCreateFixedAsset}
                    glAccountOptions={glAccounts}
                    glAccountsLoading={glAccountsLoading}
                />
            </div>
        </SubledgerProvider>
    );
}
